import { apiService } from '../../../components/api/apiService';
import { logDataModels } from './dataModelsLogger';

const CONNECTION = 'backend_py';
const PATH = '/api/v1/trino/table-preview';

const IDENTIFIER_RE = /^[A-Za-z_][A-Za-z0-9_]*$/;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function quotedRelation(tableName: string, path?: string): string {
  const raw = (path || tableName || '').trim();
  const parts = raw.split('.').map((part) => part.trim()).filter(Boolean);
  // Trino reads a.b.c as catalog.schema.table. Stored JDBC paths are
  // database.schema.table; the connection catalog is sent separately.
  const relationParts = parts.length > 2 ? parts.slice(-2) : parts;
  return relationParts.map((part) => (IDENTIFIER_RE.test(part) ? `"${part}"` : part)).join('.');
}

function sqlString(value: string): string {
  return `'${value.replace(/'/g, "''")}'`;
}

export function buildAssetSelectSql(tableName: string, path?: string): string {
  const quoted = quotedRelation(tableName, path);
  return quoted ? `SELECT * FROM ${quoted} LIMIT 100` : '';
}

export function buildAssetSearchSql(tableName: string, path: string | undefined, columns: string[], term: string): string {
  const relation = quotedRelation(tableName, path);
  const text = (term || '').trim();
  const names = columns.map((name) => name.trim()).filter((name) => IDENTIFIER_RE.test(name));
  if (!relation || !text) return '';
  if (UUID_RE.test(text)) {
    const literal = sqlString(text);
    const exact = sqlString(text.toLowerCase());
    const clauses = (names.length ? names : ['id']).flatMap((name) => [
      `TRY_CAST("${name}" AS UUID) = CAST(${literal} AS UUID)`,
      `lower(TRY_CAST("${name}" AS VARCHAR)) = ${exact}`,
    ]);
    return `SELECT * FROM ${relation} WHERE (${clauses.join(' OR ')})`;
  }
  const like = sqlString(`%${text.replace(/\\/g, '\\\\').replace(/%/g, '\\%').replace(/_/g, '\\_')}%`);
  const searchable = names.length ? names : ['id'];
  const clauses = searchable.map(
    (name) => `lower(TRY_CAST("${name}" AS VARCHAR)) LIKE lower(${like}) ESCAPE '\\'`,
  );
  return `SELECT * FROM ${relation} WHERE (${clauses.join(' OR ')})`;
}

export const PREVIEW_PAGE_SIZES = [25, 50, 100] as const;
export const DEFAULT_PREVIEW_PAGE_SIZE = 25;

export interface TablePreviewColumn {
  name: string;
  type: string;
}

export interface TablePreviewResponse {
  catalog: string;
  columns: TablePreviewColumn[];
  rows: Record<string, unknown>[];
  rowCount: number;
  page: number;
  pageSize: number;
  total: number;
  hasMore: boolean;
  truncated: boolean;
}

function publicErrorMessage(message: string, fallback: string): string {
  const cleaned = (message || '')
    .replace(/\btrino\s+preview\b/gi, 'live preview')
    .replace(/\btrino\s+/gi, '')
    .replace(/\btrino\b/gi, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
  if (!cleaned) return fallback;
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
}

function serviceErrorMessage(err: unknown, fallback: string): string {
  const axiosErr = err as {
    response?: { data?: { error?: { message?: string }; message?: string } };
    message?: string;
  };
  return publicErrorMessage(
    axiosErr.response?.data?.error?.message || axiosErr.response?.data?.message || axiosErr.message || '',
    fallback,
  );
}

function asColumns(raw: unknown, rows: Record<string, unknown>[]): TablePreviewColumn[] {
  if (Array.isArray(raw)) {
    const columns = raw
      .map((item) => {
        if (typeof item === 'string' && item.trim()) return { name: item.trim(), type: 'unknown' };
        if (!item || typeof item !== 'object') return { name: '', type: 'unknown' };
        const row = item as Record<string, unknown>;
        return { name: String(row.name || ''), type: String(row.type || 'unknown') };
      })
      .filter((column) => column.name);
    if (columns.length) return columns;
  }
  const first = rows[0];
  return first ? Object.keys(first).map((name) => ({ name, type: 'unknown' })) : [];
}

function asRows(raw: unknown): Record<string, unknown>[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter((item): item is Record<string, unknown> => Boolean(item) && typeof item === 'object' && !Array.isArray(item));
}

export async function searchTablePreview(input: {
  sourceId: string;
  tableName: string;
  path?: string;
  columns?: string[];
  search: string;
  page?: number;
  pageSize?: number;
}): Promise<TablePreviewResponse> {
  const page = Math.max(1, input.page || 1);
  const pageSize = PREVIEW_PAGE_SIZES.includes(input.pageSize as (typeof PREVIEW_PAGE_SIZES)[number])
    ? Number(input.pageSize)
    : DEFAULT_PREVIEW_PAGE_SIZE;
  const query = buildAssetSearchSql(input.tableName, input.path, input.columns || [], input.search);
  if (!query) {
    throw new Error('Search is required');
  }
  logDataModels('app', 'table_preview_search_query', {
    sourceId: input.sourceId,
    tableName: input.tableName,
    searchLen: input.search.trim().length,
    columns: (input.columns || []).length,
    page,
    pageSize,
  });
  const result = await runAssetQuery({
    sourceId: input.sourceId,
    tableName: input.tableName,
    path: input.path,
    query,
  });
  const total = result.rows.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize) || 1);
  const current = Math.min(page, totalPages);
  const start = (current - 1) * pageSize;
  return {
    catalog: '',
    columns: result.columns,
    rows: result.rows.slice(start, start + pageSize),
    rowCount: Math.min(pageSize, Math.max(0, total - start)),
    page: current,
    pageSize,
    total,
    hasMore: start + pageSize < total,
    truncated: false,
  };
}

export async function runAssetQuery(input: {
  sourceId: string;
  tableName: string;
  path?: string;
  query: string;
}): Promise<{ columns: TablePreviewColumn[]; rows: Record<string, unknown>[] }> {
  logDataModels('app', 'asset_query', { sourceId: input.sourceId, tableName: input.tableName });
  try {
    const data = await apiService.post(
      '/api/v1/trino/run-query',
      { sourceId: input.sourceId, tableName: input.tableName, path: input.path || undefined, query: input.query },
      CONNECTION,
    );
    const record = data && typeof data === 'object' ? (data as Record<string, unknown>) : {};
    const rows = asRows(record.rows);
    const columns = asColumns(record.columns, rows);
    logDataModels('app', 'asset_query_ok', {
      sourceId: input.sourceId,
      tableName: input.tableName,
      columns: columns.length,
      rows: rows.length,
    });
    return { columns, rows };
  } catch (err) {
    const message = serviceErrorMessage(err, 'Unable to run query');
    logDataModels('error', 'asset_query_failed', { sourceId: input.sourceId, tableName: input.tableName, message });
    throw new Error(message);
  }
}

export async function fetchTablePreview(input: {
  sourceId: string;
  tableName: string;
  path?: string;
  page?: number;
  pageSize?: number;
  search?: string;
  columns?: string[];
}): Promise<TablePreviewResponse> {
  const page = Math.max(1, input.page || 1);
  const pageSize = PREVIEW_PAGE_SIZES.includes(input.pageSize as (typeof PREVIEW_PAGE_SIZES)[number])
    ? Number(input.pageSize)
    : DEFAULT_PREVIEW_PAGE_SIZE;
  const search = (input.search || '').trim().slice(0, 120);
  const searchColumns = (input.columns || []).map((name) => name.trim()).filter(Boolean).slice(0, 400);
  logDataModels('app', 'table_preview', {
    sourceId: input.sourceId,
    tableName: input.tableName,
    hasPath: Boolean(input.path),
    page,
    pageSize,
    hasSearch: Boolean(search),
    searchLen: search.length,
    columns: searchColumns.length,
  });
  try {
    const data = await apiService.post(
      PATH,
      {
        sourceId: input.sourceId,
        tableName: input.tableName,
        path: input.path || undefined,
        page,
        pageSize,
        search: search || undefined,
        columns: search && searchColumns.length ? searchColumns : undefined,
      },
      CONNECTION,
    );
    const record = data && typeof data === 'object' ? (data as Record<string, unknown>) : {};
    const rows = asRows(record.rows);
    const columns = asColumns(record.columns, rows);
    const result: TablePreviewResponse = {
      catalog: String(record.catalog || ''),
      columns,
      rows,
      rowCount: Number(record.rowCount ?? record.row_count ?? rows.length) || 0,
      page: Number(record.page ?? page) || page,
      pageSize: Number(record.pageSize ?? record.page_size ?? pageSize) || pageSize,
      total: Number(record.total ?? 0) || 0,
      hasMore: Boolean(record.hasMore ?? record.has_more),
      truncated: Boolean(record.truncated),
    };
    logDataModels('app', 'table_preview_ok', {
      sourceId: input.sourceId,
      tableName: input.tableName,
      catalog: result.catalog,
      columns: result.columns.length,
      rows: result.rowCount,
      page: result.page,
      pageSize: result.pageSize,
      total: result.total,
      hasMore: result.hasMore,
    });
    return result;
  } catch (err) {
    const message = serviceErrorMessage(err, 'Unable to load live table preview');
    logDataModels('error', 'table_preview_failed', {
      sourceId: input.sourceId,
      tableName: input.tableName,
      page,
      pageSize,
      message,
    });
    throw new Error(message);
  }
}

export function formatPreviewCell(value: unknown): string {
  if (value === null || value === undefined) return 'NULL';
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
}
